import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, Copy, Landmark, Paperclip, X, FileCheck2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { scrollToId } from "@/lib/scroll";
import { CLUB, TIERS, BANK } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contatti({ selectedTier }) {
  const [form, setForm] = useState({
    nome: "", cognome: "", email: "", telefono: "", citta: "", tier: TIERS[0].id, note: "",
  });
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      toast.error("Formato non valido. Allega un PDF, JPG o PNG.");
      e.target.value = "";
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("File troppo grande (max 8 MB).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  useEffect(() => {
    if (selectedTier) {
      setForm((f) => ({ ...f, tier: selectedTier.id }));
      setTimeout(() => scrollToId("#contatti"), 60);
    }
  }, [selectedTier]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let receipt = {};
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/upload-receipt`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        receipt = { receipt_id: up.data.id, receipt_filename: up.data.filename };
      }
      await axios.post(`${API}/membership`, { ...form, ...receipt });
      toast.success("Richiesta inviata!", { description: "Grazie! Ti contatteremo presto per completare l'iscrizione." });
      setForm({ nome: "", cognome: "", email: "", telefono: "", citta: "", tier: TIERS[0].id, note: "" });
      setFile(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Si è verificato un errore. Controlla i dati e riprova.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const tierLabel = (t) => `${t.name} ${t.kit} — €${t.price}`;

  return (
    <section id="contatti" data-testid="contatti-section" className="bg-[#0B0E14] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-bold uppercase tracking-widest text-gold">Iscrizione & Contatti</span>
          <h2 className="mt-3 font-display font-extrabold uppercase tracking-tight text-3xl md:text-4xl text-white">
            Diventa socio nerazzurro
          </h2>
          <p className="mt-4 text-white/70 font-medium leading-relaxed">
            Compila il modulo per richiedere la tessera o avere informazioni.
          </p>

          <div data-testid="bonifico-details" className="mt-6 rounded-2xl border border-[#1E2638] bg-[#131822] p-5">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-gold">
              <Landmark size={16} /> Pagamento quota tramite bonifico
            </p>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                <span className="w-32 shrink-0 font-semibold text-white/50 uppercase text-xs tracking-wider">Intestatario</span>
                <span className="font-semibold text-white">{BANK.intestatario}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start sm:gap-3">
                <span className="w-32 shrink-0 font-semibold text-white/50 uppercase text-xs tracking-wider">IBAN</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-white break-all">{BANK.iban}</span>
                  <button
                    type="button"
                    data-testid="copy-iban-button"
                    onClick={() => { navigator.clipboard?.writeText(BANK.iban); toast.success("IBAN copiato!"); }}
                    className="shrink-0 rounded-md border border-[#1E2638] p-1.5 text-white/60 hover:text-gold hover:border-gold transition-colors"
                    aria-label="Copia IBAN"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            </div>
            <p className="mt-4 text-xs text-white/50 leading-relaxed">
              Causale consigliata: <span className="text-white/70">"Tessera 2026/2027 – Nome Cognome"</span>. Dopo il bonifico la tua iscrizione verrà confermata dal direttivo.
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <a href={`mailto:${CLUB.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-white/80 hover:text-gold transition-colors">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary"><Mail size={18} /></span>
              {CLUB.email}
            </a>
            <p className="flex items-center gap-3 text-white/80">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary"><MapPin size={18} /></span>
              {CLUB.address}
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-2xl border border-[#1E2638]">
            <iframe
              title="Mappa Guardialfiera"
              src="https://www.google.com/maps?q=Via+Dante+Alighieri+23,+86030+Guardialfiera+CB,+Italy&output=embed"
              className="w-full h-56"
              loading="lazy"
            />
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-registration-form"
          onSubmit={submit}
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-[#1E2638] bg-[#131822] p-6 md:p-8 h-fit"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome" testid="member-input-name" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} />
            <Field label="Cognome" testid="member-input-cognome" value={form.cognome} onChange={(v) => setForm({ ...form, cognome: v })} />
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Email" type="email" testid="member-input-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Telefono" testid="member-input-phone" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
          </div>
          <div className="mt-4">
            <Field label="Città" required={false} testid="member-input-citta" value={form.citta} onChange={(v) => setForm({ ...form, citta: v })} />
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-white/80">Tessera desiderata</label>
            <select
              data-testid="member-select-tier"
              value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              className="mt-1 w-full rounded-lg border border-[#1E2638] bg-[#0B0E14] px-4 py-2.5 text-white outline-none focus:border-primary"
            >
              {TIERS.map((t) => (
                <option key={t.id} value={t.id}>{tierLabel(t)}</option>
              ))}
            </select>
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-white/80">Note (facoltativo)</label>
            <textarea
              data-testid="member-input-note"
              rows={3}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="mt-1 w-full rounded-lg border border-[#1E2638] bg-[#0B0E14] px-4 py-2.5 text-white outline-none focus:border-primary resize-none"
              placeholder="Scrivici un messaggio..."
            />
          </div>
          <div className="mt-4">
            <label className="text-sm font-semibold text-white/80">Allega copia del bonifico <span className="text-white/40 font-normal">(PDF o JPG, facoltativo)</span></label>
            {!file ? (
              <label
                data-testid="member-receipt-dropzone"
                className="mt-1 flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#2a3550] bg-[#0B0E14] px-4 py-3.5 text-white/60 hover:border-primary hover:text-white transition-colors"
              >
                <Paperclip size={18} />
                <span className="text-sm font-semibold">Scegli un file (PDF, JPG, PNG — max 8 MB)</span>
                <input
                  data-testid="member-input-receipt"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={onFile}
                  className="hidden"
                />
              </label>
            ) : (
              <div data-testid="member-receipt-selected" className="mt-1 flex items-center justify-between gap-3 rounded-lg border border-primary/50 bg-[#0B0E14] px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-white truncate">
                  <FileCheck2 size={18} className="shrink-0 text-emerald-400" />
                  <span className="truncate">{file.name}</span>
                </span>
                <button
                  type="button"
                  data-testid="member-receipt-remove"
                  onClick={() => setFile(null)}
                  className="shrink-0 rounded-md p-1 text-white/60 hover:text-red-400 transition-colors"
                  aria-label="Rimuovi allegato"
                >
                  <X size={18} />
                </button>
              </div>
            )}
          </div>
          <button
            data-testid="member-form-submit"
            type="submit"
            disabled={loading}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3.5 font-bold uppercase tracking-wide text-[#0B0E14] hover:brightness-110 transition-all disabled:opacity-60"
          >
            <Send size={17} /> {loading ? "Invio in corso..." : "Invia richiesta di iscrizione"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, required = true }) {
  return (
    <div>
      <label className="text-sm font-semibold text-white/80">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-[#1E2638] bg-[#0B0E14] px-4 py-2.5 text-white outline-none focus:border-primary"
      />
    </div>
  );
}
